import React from "react";
import { connect } from "react-redux";
import { Form, Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";

import ConditionalButton from "../../../layout/conditional-button";
import DeleteModal from "../../../layout/modal";

import { isGuid } from "../../../utils/string";
import { withNavigation } from "../../../utils/wrapper";

import { send as pageInfo, loading } from "../../../actions/page";
import { get as getConfigurations, save as saveConfiguration, destroy } from "../../../actions/email.configuration";
import { get as getSetting } from "../../../actions/settings";
import { send as sendAlert } from "../../../actions/alert";

const DEFAULT_PASSWORD = "********";

class Settings extends React.Component {
    constructor(props) {
        super(props);

        this.props.loading(true);
        this.state = {
            ...this.propsToState(props),
            errors: {},
            modal: {
                delete: {
                    show: false
                }
            }
        };

        this.props.pageInfo({ title: "Email Settings", breadcrumb: this.state?.name });
    }

    get uuid() {
        return this.props.params.uuid;
    }

    propsToState = (props) => {
        let { uuid, name, system, host, port, secure, user, password } = props;

        if (!isGuid(this.uuid)) {
            uuid = uuid || "";
            name = name ?? "[New Configuration]";
            system = system ?? false;
            host = host ?? "";
            port = port ?? 587;
            secure = secure ?? false;
            user = user ?? "";
            password = password ?? DEFAULT_PASSWORD;
        } else {
            // editing path defaults
            uuid = uuid || this.uuid;
            name = name ?? "";
            system = system ?? false;
            host = host ?? "";
            port = port ?? 587;
            secure = secure ?? false;
            user = user ?? "";
            password = password ?? DEFAULT_PASSWORD;
        }

        return { uuid, name, system, host, port, secure, user, password };
    };

    loadData = async () => {
        this.props.loading(true);
        try {
            this.configurations = await this.props.getConfigurations();
            const system = await this.props.getSetting({ name: "FERA_EMAIL_CONFIGURATION" });

            if (isGuid(this.uuid) && Array.isArray(this.configurations)) {
                const active = this.configurations.find((item) => item.uuid === this.uuid);

                this.props.pageInfo({
                    title: "Email Settings",
                    breadcrumb: active?.name || "[Configuration]"
                });

                if (active) {
                    this.setState({
                        ...active,
                        system: system?.value === active?.name,
                        password: DEFAULT_PASSWORD, // keep masked unless user changes
                        errors: {}
                    });
                }
            } else {
                this.props.pageInfo({
                    title: "Email Settings",
                    breadcrumb: this.state?.name || "[New Configuration]"
                });
            }
        } catch (err) {
            this.props.sendAlert(err?.message || "Unable to load email settings.", "error");
        } finally {
            this.props.loading(false);
        }
    };

    componentDidMount = async () => {
        await this.loadData();
    };

    validate = () => {
        const errors = {};

        const name = String(this.state.name ?? "").trim();
        const host = String(this.state.host ?? "").trim();
        const portRaw = this.state.port;
        const port = Number(portRaw);

        if (!name) errors.name = "Configuration caption is required.";
        if (!host) errors.host = "SMTP relay host is required.";
        if (!Number.isFinite(port) || port < 1 || port > 65535) {
            errors.port = "SMTP port must be a number between 1 and 65535.";
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    };

    onSaveClick = async () => {
        const { isValid, errors } = this.validate();
        this.setState({ errors });

        if (!isValid) {
            if (errors.name) this.props.sendAlert(errors.name, "error");
            if (errors.host) this.props.sendAlert(errors.host, "error");
            if (errors.port) this.props.sendAlert(errors.port, "error");
            return;
        }

        this.props.loading(true);
        try {
            const values = this.propsToState(this.state);

            values.name = String(values.name ?? "").trim();
            values.host = String(values.host ?? "").trim();
            values.user = String(values.user ?? "").trim();
            values.port = Number(values.port);
            values.system = !!values.system;
            values.secure = !!values.secure;

            if (!isGuid(values.uuid)) delete values.uuid;
            if (values.password === DEFAULT_PASSWORD) delete values.password;

            const result = await this.props.saveConfiguration(values);
            window.location.href = `/dashboard/settings/email/${result.uuid}`; // hard reload by preference
        } catch (err) {
            this.props.sendAlert(err?.message || "Unable to save configuration.", "error");
        } finally {
            this.props.loading(false);
        }
    };

    onChange = (e) => {
        const { name, value } = e.target;
        this.setState((prev) => ({
            [name]: value,
            errors: {
                ...prev.errors,
                [name]: false
            }
        }));
    };

    onCheckChange = (e) => {
        const { name, checked } = e.target;
        this.setState({
            [name]: checked
        });
    };

    onDeleteClick = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                delete: {
                    title: "Delete configuration",
                    body: `This action will permanently delete the configuration named ${this.state.name}. If this configuration is the default, you will need to select (or create) a new default before any new messages will be able to be sent.`,
                    show: true
                }
            }
        }));
    };

    onDeleteOkModal = async () => {
        this.props.loading(true);
        try {
            await this.props.destroy(this.state.uuid);
            this.onDeleteHideModal();
            window.location.href = "/dashboard/settings/email"; // hard reload by preference
        } catch (err) {
            this.props.sendAlert(err?.message || "Unable to delete configuration.", "error");
        } finally {
            this.props.loading(false);
        }
    };

    onDeleteHideModal = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                delete: {
                    show: false
                }
            }
        }));
    };

    render() {
        return (
            <>
                <Col md="8" className="float-right text-end">
                    <ConditionalButton
                        enabledVariant="outline-primary"
                        tooltip="Save Configuration"
                        display={true}
                        enabled={true}
                        onEnabledClick={this.onSaveClick}
                        icon="save"
                    >
                        Save
                    </ConditionalButton>

                    <ConditionalButton
                        enabledVariant="outline-primary"
                        tooltip="Delete Configuration"
                        display={true}
                        enabled={isGuid(this.state.uuid)}
                        onEnabledClick={this.onDeleteClick}
                        icon="bin"
                    >
                        Delete
                    </ConditionalButton>
                </Col>

                <Col xs="12">
                    <Row>
                        <Col lg="12">
                            <Row className="g-3">
                                <Col lg="12">
                                    <h2 className="small-title">Configuration Information</h2>
                                </Col>
                            </Row>

                            <Row>
                                <Col xs="12" lg="8">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={this.state.name || ""}
                                            onChange={this.onChange}
                                            isInvalid={!!this.state.errors?.name}
                                        />
                                        <Form.Label>CAPTION</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            Configuration caption is required.
                                        </Form.Control.Feedback>
                                    </div>
                                </Col>

                                <Col xs="12" lg="4">
                                    <div className="mb-3">
                                        <Form.Check
                                            onChange={this.onCheckChange}
                                            type="switch"
                                            checked={!!this.state.system}
                                            name="system"
                                            label="Use as system mailer?"
                                        />
                                    </div>
                                </Col>

                                <Col xs="12" md="8" lg="6">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="text"
                                            name="host"
                                            value={this.state.host || ""}
                                            onChange={this.onChange}
                                            required={true}
                                            minLength={1}
                                            maxLength={320}
                                            isInvalid={!!this.state.errors?.host}
                                        />
                                        <Form.Label>SMTP RELAY HOST NAME (OMIT PROTOCOL)</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            Please enter a valid relay host
                                        </Form.Control.Feedback>
                                    </div>
                                </Col>

                                <Col xs="12" md="4" lg="2">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="number"
                                            name="port"
                                            value={this.state.port ?? 587}
                                            onChange={this.onChange}
                                            required={true}
                                            max={65535}
                                            min={1}
                                            isInvalid={!!this.state.errors?.port}
                                        />
                                        <Form.Label>PORT</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            Please enter a valid port
                                        </Form.Control.Feedback>
                                    </div>
                                </Col>

                                <Col xs="12" lg="4">
                                    <div className="mb-3">
                                        <Form.Check
                                            onChange={this.onCheckChange}
                                            type="switch"
                                            checked={!!this.state.secure}
                                            name="secure"
                                            label="Secure?"
                                        />
                                    </div>
                                </Col>

                                <Col xs="12" md="6" lg="4">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="text"
                                            name="user"
                                            value={this.state.user || ""}
                                            onChange={this.onChange}
                                        />
                                        <Form.Label>USER NAME</Form.Label>
                                    </div>
                                </Col>

                                <Col xs="12" md="6" lg="4">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="password"
                                            name="password"
                                            value={this.state.password || ""}
                                            onChange={this.onChange}
                                        />
                                        <Form.Label>PASSWORD</Form.Label>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Col>

                <DeleteModal
                    show={this.state.modal.delete.show}
                    title={this.state.modal.delete.title}
                    body={this.state.modal.delete.body}
                    buttons={[
                        {
                            caption: "ok",
                            onEnabledClick: this.onDeleteOkModal,
                            variant: "primary",
                            display: true,
                            enabled: true
                        },
                        {
                            default: true,
                            caption: "cancel",
                            onEnabledClick: this.onDeleteHideModal,
                            variant: "secondary",
                            display: true,
                            enabled: true
                        }
                    ]}
                />
            </>
        );
    }
}

Settings.propTypes = {
    auth: PropTypes.object.isRequired,
    params: PropTypes.object.isRequired,
    pageInfo: PropTypes.func.isRequired,
    loading: PropTypes.func.isRequired,
    getConfigurations: PropTypes.func.isRequired,
    getSetting: PropTypes.func.isRequired,
    saveConfiguration: PropTypes.func.isRequired,
    destroy: PropTypes.func.isRequired,
    sendAlert: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default withNavigation(
    connect(mapStateToProps, {
        pageInfo,
        loading,
        getConfigurations,
        getSetting,
        saveConfiguration,
        destroy,
        sendAlert
    })(Settings)
);